import {
  ImageNotSupported,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  ImageList,
  ImageListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WOQuality } from '../../common/enums/enums';
import PhotoGalleryPopUp from '../PhotoGalleryPopUp';

const DealerReview = ({ data }) => {
  const { t } = useTranslation('workOrdersTable');

  const Emotions = ({ area }) => {
    return (
      <Stack direction="row" spacing={3} pt={1}>
        <SentimentVeryDissatisfied
          fontSize="large"
          color={area === WOQuality.BELOW ? 'error' : 'disabled'}
        />
        <SentimentSatisfied
          fontSize="large"
          color={area === WOQuality.MEETS ? 'warning' : 'disabled'}
        />
        <SentimentVerySatisfied
          fontSize="large"
          color={area === WOQuality.ABOVE ? 'success' : 'disabled'}
        />
      </Stack>
    );
  };

  return (
    <Stack>
      <Typography variant="subtitle1">
        {t('detail.qualitySurvey.dealerReviews')}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t('detail.qualitySurvey.workQuality')}{' '}
        {t(
          `detail.qualitySurvey.workQualityCaptions.${data?.qualitySurvey?.workQuality}`
        )}
      </Typography>
      <Emotions area={data?.qualitySurvey?.workQuality} />

      <Typography variant="body1" color="text.secondary" pt={2}>
        {t('detail.qualitySurvey.deliveryTime')}{' '}
        {t(
          `detail.qualitySurvey.deliveryTimeCaptions.${data?.qualitySurvey?.deliveryTime}`
        )}
      </Typography>
      <Emotions area={data?.qualitySurvey?.deliveryTime} />
    </Stack>
  );
};

const Photo = ({ photo, onClick }) => {
  return (
    (photo && (
      <img
        src={photo}
        alt="Quality"
        style={{ maxHeight: '70px', maxWidth: '70px', cursor: 'pointer' }}
        data-testid="surveyImages"
        onClick={onClick}
      />
    )) || (
      <Box
        width="70px"
        height="70px"
        sx={{ background: theme => theme.palette.grey[200] }}
      ></Box>
    )
  );
};

const Photos = ({ data, onClick }) => {
  const { t } = useTranslation('workOrdersTable');
  const MAX_IMAGES_ALLOWED = 6;
  const surveyImages = Array.from({ length: MAX_IMAGES_ALLOWED });

  return (
    <Stack>
      <Typography variant="subtitle1">
        {t('detail.qualitySurvey.photos')}
      </Typography>
      <Stack spacing={2}>
        {(data?.qualitySurvey?.images &&
          data?.qualitySurvey?.images.length && (
            <ImageList
              sx={{ width: 242, height: 156, m: 0 }}
              gap={10}
              cols={3}
              rowHeight={70}
            >
              {surveyImages.map((item, index) => (
                <ImageListItem
                  key={`photo-${index}`}
                  sx={{ justifyContent: 'center' }}
                >
                  <Photo
                    photo={data?.qualitySurvey?.images[index]}
                    onClick={() => onClick(index)}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )) || (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="242px"
            height="156px"
            sx={{ background: 'rgba(33, 150, 243, 0.08)' }}
          >
            <ImageNotSupported fontSize="large" color="info" />
            <Typography variant="body2">
              {t('detail.qualitySurvey.noPhotos')}
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

const Comments = ({ data }) => {
  const { t } = useTranslation('workOrdersTable');

  return (
    <Stack>
      <Typography variant="subtitle1">
        {t('detail.qualitySurvey.additionalComments')}
      </Typography>
      <TextField
        multiline
        rows={5}
        variant="outlined"
        sx={{ background: theme => theme.palette.grey[50] }}
        value={data?.qualitySurvey?.additionalComments}
        disabled
      />
    </Stack>
  );
};

const WOrderQualitySurvey = ({ data }) => {
  const [initialPhotoIndex, setInitialPhotoIndex] = useState(null);

  const openPhotoGallery = index => {
    setInitialPhotoIndex(index);
  };

  return (
    (data?.qualitySurvey && (
      <>
        <Box sx={{ bgcolor: 'background.paper' }}>
          <Divider />
          <Box p={{ xs: 2, lg: 4 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              divider={
                <Divider
                  orientation="vertical"
                  sx={{ marginLeft: 0 }}
                  flexItem
                />
              }
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2.25}
                flex="1 1 50%"
              >
                <DealerReview data={data} />
                <Photos
                  data={data}
                  onClick={index => openPhotoGallery(index)}
                />
              </Stack>
              <Stack flex="1 1 50%">
                <Comments data={data} />
              </Stack>
            </Stack>
          </Box>
        </Box>
        <PhotoGalleryPopUp
          open={initialPhotoIndex !== null}
          images={data?.qualitySurvey?.images}
          handleModalState={() => openPhotoGallery(null)}
          initialSlide={initialPhotoIndex}
        />
      </>
    )) || <></>
  );
};

export default WOrderQualitySurvey;
